import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardHeader,
  FormField,
  InlineAlert,
  SkeletonRows,
  TextInput,
} from "@constructorfleet/ui/design-system";

export type AdminStatesManagerProps = {
  requestAdmin?: <T = unknown>(input: {
    method: "GET" | "POST" | "DELETE";
    path: string;
    body?: unknown;
  }) => Promise<{ ok: true; data: T } | { ok: false; error: string }>;
};

export type SupportedState = { code: string; name: string };

export function AdminStatesManager({ requestAdmin }: AdminStatesManagerProps) {
  const [loading, setLoading] = useState(true);
  const [states, setStates] = useState<SupportedState[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!requestAdmin) {
        setLoading(false);
        return;
      }
      setLoading(true);
      const resp = await requestAdmin<SupportedState[]>({
        method: "GET",
        path: "/api/admin/co-ua/states",
      });
      if (!mounted) return;
      if (!resp.ok) {
        setError(resp.error);
      } else {
        setStates(Array.isArray(resp.data) ? (resp.data as any) : []);
      }
      setLoading(false);
    })();
    return () => {
      mounted = false;
    };
  }, [requestAdmin]);

  async function refresh() {
    if (!requestAdmin) return;
    setLoading(true);
    const resp = await requestAdmin<SupportedState[]>({
      method: "GET",
      path: "/api/admin/co-ua/states",
    });
    if (!resp.ok) setError(resp.error);
    else setStates(Array.isArray(resp.data) ? (resp.data as any) : []);
    setLoading(false);
  }

  async function handleAdd(e?: React.FormEvent) {
    e?.preventDefault();
    if (!requestAdmin) return;
    setSaving(true);
    setError(null);
    const payload = { code: code.trim().toUpperCase(), name: name.trim() };
    const resp = await requestAdmin({
      method: "POST",
      path: "/api/admin/co-ua/states",
      body: payload,
    });
    if (!resp.ok) {
      setError(resp.error);
    } else {
      setCode("");
      setName("");
      await refresh();
    }
    setSaving(false);
  }

  async function handleDelete(codeToDelete: string) {
    if (!requestAdmin) return;
    setSaving(true);
    setError(null);
    const resp = await requestAdmin({
      method: "DELETE",
      path: `/api/admin/co-ua/states/${encodeURIComponent(codeToDelete)}`,
    });
    if (!resp.ok) {
      setError(resp.error);
    } else {
      await refresh();
    }
    setSaving(false);
  }

  if (!requestAdmin) {
    return (
      <InlineAlert tone="info">States management is not available in this context.</InlineAlert>
    );
  }

  return (
    <Card wide>
      <CardHeader
        title="Supported States"
        meta="Add, list, and remove supported states and jurisdictions."
      />
      <div className="card-body ds-stack">
        {error ? <InlineAlert tone="danger">{error}</InlineAlert> : null}

        <form onSubmit={handleAdd} className="ds-stack" style={{ gap: "0.5rem" }}>
          <div style={{ display: "flex", gap: "0.5rem", alignItems: "flex-end" }}>
            <FormField label="Code" htmlFor="state-code">
              <TextInput
                id="state-code"
                value={code}
                onChange={(e) => setCode((e.target as HTMLInputElement).value)}
              />
            </FormField>

            <FormField label="Name" htmlFor="state-name">
              <TextInput
                id="state-name"
                value={name}
                onChange={(e) => setName((e.target as HTMLInputElement).value)}
              />
            </FormField>

            <div>
              <Button type="submit" disabled={saving || !code || !name}>
                {saving ? "Saving..." : "Add state"}
              </Button>
            </div>
          </div>
        </form>

        <div style={{ marginTop: "1rem" }}>
          {loading ? (
            <SkeletonRows count={3} />
          ) : states.length === 0 ? (
            <div>No supported states configured.</div>
          ) : (
            <table className="ds-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Name</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {states.map((s) => (
                  <tr key={s.code}>
                    <td>{s.code}</td>
                    <td>{s.name}</td>
                    <td>
                      <Button className="btn-danger" onClick={() => handleDelete(s.code)}>
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </Card>
  );
}

export default AdminStatesManager;
